import { CheckCircleOutlined, ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Modal as _Modal } from 'antd';
import { ModalProps as $ModalProps, ModalFuncProps as $ModalFuncProps } from 'antd/lib/modal';
import React, { useCallback, useMemo, useState, SetStateAction, Dispatch, ReactNode } from 'react';

import { Button, ButtonProps } from '.';

import { ModalFooterWrapper, Modal as $Modal } from './common/antd/antd-override';

interface ButtonPropsOverride {
  okType?: ButtonProps['type'];
  okButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
}

type OmitPropKeys = 'okType' | 'okButtonProps' | 'cancelButtonProps';

export type ModalProps = Omit<$ModalProps, OmitPropKeys> &
  ButtonPropsOverride & {
    children?: ReactNode;
  };
export type ModalFuncProps = Omit<$ModalFuncProps, OmitPropKeys> & ButtonPropsOverride;

export const OK_BUTTON_DATA_TEST_ID = 'ok-button';
export const CANCEL_BUTTON_DATA_TEST_ID = 'cancel-button';

const OK_BUTTON_CLASS_NAME = 'ok-btn';
const CANCEL_BUTTON_CLASS_NAME = 'cancel-btn';

const DEFAULT_MODAL_WIDTH = 736;

const ModalFooter = ({
  onCancel,
  cancelButtonProps,
  cancelText,
  okType,
  confirmLoading,
  onOk,
  okButtonProps,
  okText,
}: any) => {
  const [loading, setLoading] = useState(confirmLoading);
  const handleOnOKPress = useCallback(async () => {
    try {
      setLoading(true);
      await onOk();
    } catch {
      //
    } finally {
      setLoading(false);
    }
  }, [onOk]);

  return (
    <div>
      {onCancel && (
        <Button data-testid={CANCEL_BUTTON_DATA_TEST_ID} onClick={onCancel} {...cancelButtonProps}>
          {cancelText}
        </Button>
      )}
      <Button
        data-testid={OK_BUTTON_DATA_TEST_ID}
        type={okType}
        loading={confirmLoading || loading}
        onClick={handleOnOKPress}
        {...okButtonProps}
      >
        {okText}
      </Button>
    </div>
  );
};

const Modal = function ModalComponent({
  footer,
  okType = 'primary-blue',
  okText = 'OK',
  cancelText = 'Cancel',
  okButtonProps,
  cancelButtonProps,
  confirmLoading,
  width,
  onOk,
  onCancel,
  ...rest
}: ModalProps) {
  const okButtonPropsFooter = useMemo(() => ({ className: OK_BUTTON_CLASS_NAME, ...okButtonProps }), [okButtonProps]);
  const cancelButtonPropsFooter = useMemo(
    () => ({ className: CANCEL_BUTTON_CLASS_NAME, ...cancelButtonProps }),
    [cancelButtonProps]
  );

  return (
    <$Modal
      footer={
        footer === undefined ? (
          <ModalFooter
            confirmLoading={confirmLoading}
            okType={okType}
            okButtonProps={okButtonPropsFooter}
            cancelButtonProps={cancelButtonPropsFooter}
            okText={okText}
            cancelText={cancelText}
            onOk={onOk}
            onCancel={onCancel}
          />
        ) : (
          footer
        )
      }
      onCancel={onCancel}
      width={width ?? DEFAULT_MODAL_WIDTH}
      {...rest}
    />
  );
};

const customIconMap = {
  info: <InfoCircleOutlined />,
  confirm: <ExclamationCircleOutlined />,
  success: <CheckCircleOutlined />,
  warn: <ExclamationCircleOutlined />,
  error: null,
};

function modalMethod(
  type: 'info' | 'confirm' | 'success' | 'warn' | 'error',
  { okButtonProps, cancelButtonProps, icon, onOk, onCancel, ...rest }: ModalFuncProps
) {
  let modalRef: ReturnType<typeof _Modal.confirm> | null = null;

  // bridge state between update() and footer
  let setBtnState: Dispatch<
    SetStateAction<{ okButtonProps?: ButtonProps; cancelButtonProps?: ButtonProps }>
  > = () => {};

  const FooterWithState = () => {
    const [btnState, _setBtnState] = useState<{ okButtonProps?: ButtonProps; cancelButtonProps?: ButtonProps }>({
      okButtonProps: okButtonProps,
      cancelButtonProps: cancelButtonProps,
    });
    setBtnState = _setBtnState;

    return (
      <ModalFooter
        okButtonProps={{
          className: `${OK_BUTTON_CLASS_NAME} ${type}-${OK_BUTTON_CLASS_NAME}`,
          ...btnState.okButtonProps,
        }}
        cancelButtonProps={{
          className: `${CANCEL_BUTTON_CLASS_NAME} ${type}-${CANCEL_BUTTON_CLASS_NAME}`,
          ...btnState.cancelButtonProps,
        }}
        okType="primary-blue"
        okText="OK"
        onOk={async () => {
          await onOk?.();
          modalRef?.destroy();
        }}
        {...(type === 'confirm'
          ? {
              cancelText: 'Cancel',
              onCancel: () => {
                onCancel?.();
                modalRef?.destroy();
              },
            }
          : {})}
      />
    );
  };

  modalRef = _Modal[type]({
    okType: 'primary-blue' as any,
    icon: icon ?? customIconMap[type],
    modalRender: (node) => <ModalFooterWrapper>{node}</ModalFooterWrapper>,
    footer: () => <FooterWithState />,
    width: DEFAULT_MODAL_WIDTH,
    ...rest,
  });

  // extend update() so it refreshes our custom footer
  const realUpdate = modalRef.update.bind(modalRef);
  modalRef.update = (cfg: any) => {
    // keep the native behaviour
    realUpdate(cfg);

    if (cfg?.okButtonProps || cfg?.cancelButtonProps) {
      setBtnState((prev) => ({
        okButtonProps: { ...prev.okButtonProps, ...cfg.okButtonProps },
        cancelButtonProps: { ...prev.cancelButtonProps, ...cfg.cancelButtonProps },
      }));
    }
  };

  return modalRef;
}

Modal.info = modalMethod.bind(null, 'info');
Modal.confirm = modalMethod.bind(null, 'confirm');
Modal.success = modalMethod.bind(null, 'success');
Modal.warn = modalMethod.bind(null, 'warn');
Modal.error = modalMethod.bind(null, 'error');
Modal.destroyAll = $Modal.destroyAll;

export default Modal;
